/**
 * Shared preset avatar catalog for Account page and Profile edit strip.
 * Keys and CDN URLs match `backend/config/avatarCatalog.json` / `/account/avatars`.
 */

export type AvatarCatalogItem = {
  key: string;
  animal?: string;
  url?: string;
  thumb_url?: string;
  thumb_urls?: Record<string, string>;
  thumb_srcset?: string;
};

export const AVATAR_OPTIONS_CACHE_KEY = "c8_avatar_options_cache_v1";

/** Offline / pre-fetch fallback — one preset per animal family (same as legacy Account.jsx). */
export const FALLBACK_AVATAR_OPTIONS: AvatarCatalogItem[] = [
  {
    key: "giraffe-blue",
    animal: "giraffe",
    url: "https://cricket8-web-content.s3.amazonaws.com/avatars/giraffe-blue.png",
    thumb_url: "https://cricket8-web-content.s3.amazonaws.com/avatars/thumbs/giraffe-blue.webp",
    thumb_urls: {
      "64": "https://cricket8-web-content.s3.amazonaws.com/avatars/thumbs/64/giraffe-blue.webp",
      "96": "https://cricket8-web-content.s3.amazonaws.com/avatars/thumbs/96/giraffe-blue.webp",
      "128": "https://cricket8-web-content.s3.amazonaws.com/avatars/thumbs/128/giraffe-blue.webp",
    },
  },
  {
    key: "croc-blue",
    animal: "crocodile",
    url: "https://cricket8-web-content.s3.amazonaws.com/avatars/croc-blue.png",
    thumb_url: "https://cricket8-web-content.s3.amazonaws.com/avatars/thumbs/croc-blue.webp",
    thumb_urls: {
      "64": "https://cricket8-web-content.s3.amazonaws.com/avatars/thumbs/64/croc-blue.webp",
      "96": "https://cricket8-web-content.s3.amazonaws.com/avatars/thumbs/96/croc-blue.webp",
      "128": "https://cricket8-web-content.s3.amazonaws.com/avatars/thumbs/128/croc-blue.webp",
    },
  },
  {
    key: "rhino-blue",
    animal: "rhino",
    url: "https://cricket8-web-content.s3.amazonaws.com/avatars/rhino-blue.png",
    thumb_url: "https://cricket8-web-content.s3.amazonaws.com/avatars/thumbs/rhino-blue.webp",
    thumb_urls: {
      "64": "https://cricket8-web-content.s3.amazonaws.com/avatars/thumbs/64/rhino-blue.webp",
      "96": "https://cricket8-web-content.s3.amazonaws.com/avatars/thumbs/96/rhino-blue.webp",
      "128": "https://cricket8-web-content.s3.amazonaws.com/avatars/thumbs/128/rhino-blue.webp",
    },
  },
  {
    key: "tiger-blue-red",
    animal: "tiger",
    url: "https://cricket8-web-content.s3.amazonaws.com/avatars/tiger-blue-red.png",
    thumb_url: "https://cricket8-web-content.s3.amazonaws.com/avatars/thumbs/tiger-blue-red.webp",
    thumb_urls: {
      "64": "https://cricket8-web-content.s3.amazonaws.com/avatars/thumbs/64/tiger-blue-red.webp",
      "96": "https://cricket8-web-content.s3.amazonaws.com/avatars/thumbs/96/tiger-blue-red.webp",
      "128": "https://cricket8-web-content.s3.amazonaws.com/avatars/thumbs/128/tiger-blue-red.webp",
    },
  },
  {
    key: "monkey-blue-pink",
    animal: "monkey",
    url: "https://cricket8-web-content.s3.amazonaws.com/avatars/monkey-blue-pink.png",
    thumb_url: "https://cricket8-web-content.s3.amazonaws.com/avatars/thumbs/monkey-blue-pink.webp",
    thumb_urls: {
      "64": "https://cricket8-web-content.s3.amazonaws.com/avatars/thumbs/64/monkey-blue-pink.webp",
      "96": "https://cricket8-web-content.s3.amazonaws.com/avatars/thumbs/96/monkey-blue-pink.webp",
      "128": "https://cricket8-web-content.s3.amazonaws.com/avatars/thumbs/128/monkey-blue-pink.webp",
    },
  },
  {
    key: "owl-blue",
    animal: "owl",
    url: "https://cricket8-web-content.s3.amazonaws.com/avatars/owl-blue.png",
    thumb_url: "https://cricket8-web-content.s3.amazonaws.com/avatars/thumbs/owl-blue.webp",
    thumb_urls: {
      "64": "https://cricket8-web-content.s3.amazonaws.com/avatars/thumbs/64/owl-blue.webp",
      "96": "https://cricket8-web-content.s3.amazonaws.com/avatars/thumbs/96/owl-blue.webp",
      "128": "https://cricket8-web-content.s3.amazonaws.com/avatars/thumbs/128/owl-blue.webp",
    },
  },
  {
    key: "shark-blue-pink",
    animal: "shark",
    url: "https://cricket8-web-content.s3.amazonaws.com/avatars/shark-blue-pink.png",
    thumb_url: "https://cricket8-web-content.s3.amazonaws.com/avatars/thumbs/shark-blue-pink.webp",
    thumb_urls: {
      "64": "https://cricket8-web-content.s3.amazonaws.com/avatars/thumbs/64/shark-blue-pink.webp",
      "96": "https://cricket8-web-content.s3.amazonaws.com/avatars/thumbs/96/shark-blue-pink.webp",
      "128": "https://cricket8-web-content.s3.amazonaws.com/avatars/thumbs/128/shark-blue-pink.webp",
    },
  },
  {
    key: "elephant-blue-pink",
    animal: "elephant",
    url: "https://cricket8-web-content.s3.amazonaws.com/avatars/elephant-blue-pink.png",
    thumb_url: "https://cricket8-web-content.s3.amazonaws.com/avatars/thumbs/elephant-blue-pink.webp",
    thumb_urls: {
      "64": "https://cricket8-web-content.s3.amazonaws.com/avatars/thumbs/64/elephant-blue-pink.webp",
      "96": "https://cricket8-web-content.s3.amazonaws.com/avatars/thumbs/96/elephant-blue-pink.webp",
      "128": "https://cricket8-web-content.s3.amazonaws.com/avatars/thumbs/128/elephant-blue-pink.webp",
    },
  },
];

export function getAvatarThumbSrc(avatar: AvatarCatalogItem | null | undefined): string | null {
  return (
    avatar?.thumb_urls?.["48"] ||
    avatar?.thumb_urls?.["64"] ||
    avatar?.thumb_url ||
    avatar?.url ||
    null
  );
}

const AVATAR_KEY_ANIMAL_SLUGS = new Set([
  "croc",
  "giraffe",
  "owl",
  "shark",
  "elephant",
  "monkey",
  "rhino",
  "tiger",
]);

function getAvatarColorSortKey(avatarKey: string): string {
  const k = String(avatarKey || "");
  const parts = k.split("-");
  if (parts.length < 2) return k;
  const slug = parts[0];
  if (!AVATAR_KEY_ANIMAL_SLUGS.has(slug)) return k;
  return parts.slice(1).join("-");
}

/** Same section order as Account.jsx `avatarSections`. */
const ANIMAL_SECTION_ORDER = [
  "giraffe",
  "crocodile",
  "rhino",
  "tiger",
  "monkey",
  "owl",
  "shark",
  "elephant",
] as const;

/** One catalog entry per animal section (first variant after colour sort) for compact profile strip. */
export function getAnimalPreviewAvatars(options: AvatarCatalogItem[]): AvatarCatalogItem[] {
  const grouped: Record<string, AvatarCatalogItem[]> = {};
  for (const opt of options) {
    const animal = opt.animal || "other";
    if (!grouped[animal]) grouped[animal] = [];
    grouped[animal].push(opt);
  }
  return ANIMAL_SECTION_ORDER.map((animal) => {
    const items = grouped[animal];
    if (!items?.length) return null;
    const sorted = items.slice().sort((a, b) => {
      const ca = getAvatarColorSortKey(a.key);
      const cb = getAvatarColorSortKey(b.key);
      const byColor = ca.localeCompare(cb, undefined, { sensitivity: "base" });
      if (byColor !== 0) return byColor;
      return String(a.key).localeCompare(String(b.key));
    });
    return sorted[0] ?? null;
  }).filter((x): x is AvatarCatalogItem => x != null);
}
